import { useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  TrendingUp,
  AppWindow,
  User,
  Megaphone,
  Settings,
  ShoppingCart,
  FileSignature,
  Users,
  BarChart3,
} from "lucide-react";
import { Reveal } from "./Reveal";

/**
 * Hub-and-spoke "Finance as the operating system" diagram.
 * Each satellite is clickable → scrolls to a matching "What We Bring" column.
 */
const nodes = [
  {
    id: "sales",
    label: "Sales",
    desc: "Revenue & pipeline",
    Icon: TrendingUp,
    color: "#22c55e",
    angle: -90,
    target: "wwb-strategic-finance",
  },
  {
    id: "product",
    label: "Product",
    desc: "Billing & pricing",
    Icon: AppWindow,
    color: "#15803d",
    angle: -45,
    target: "wwb-venture-building",
  },
  {
    id: "people",
    label: "People / HR",
    desc: "Hiring & headcount",
    Icon: User,
    color: "#eab308",
    angle: 0,
    target: "wwb-financial-foundation",
  },
  {
    id: "marketing",
    label: "Marketing",
    desc: "CAC & campaigns",
    Icon: Megaphone,
    color: "#7c3aed",
    angle: 45,
    target: "wwb-strategic-finance",
  },
  {
    id: "operations",
    label: "Operations",
    desc: "Cost & efficiency",
    Icon: Settings,
    color: "#ef4444",
    angle: 90,
    target: "wwb-financial-foundation",
  },
  {
    id: "vendors",
    label: "Vendors",
    desc: "Spend & terms",
    Icon: ShoppingCart,
    color: "#06b6d4",
    angle: 135,
    target: "wwb-financial-foundation",
  },
  {
    id: "investors",
    label: "Investors",
    desc: "Reporting & raises",
    Icon: FileSignature,
    color: "#ea580c",
    angle: 180,
    target: "wwb-investor-readiness",
  },
  {
    id: "leadership",
    label: "Leadership",
    desc: "Strategy & resources",
    Icon: Users,
    color: "#2563eb",
    angle: -135,
    target: "wwb-capital-network",
  },
];

// Diagram geometry inside SVG viewBox
const VB = 1000;
const CENTER = VB / 2;
const RADIUS = 340;
const HUB_RX = 150; // ellipse semi-major (horizontal)
const HUB_RY = 130; // ellipse semi-minor (vertical)
const NODE_R = 62;

const polar = (deg, r) => {
  const a = (deg * Math.PI) / 180;
  return { x: CENTER + r * Math.cos(a), y: CENTER + r * Math.sin(a) };
};

// Where to place the label (relative to the satellite container) per angle.
// Container is the satellite circle (NODE_R*2 wide, square). Origin is top-left.
const getLabelStyle = (angle) => {
  // Normalize -180..180
  if (angle === -90) {
    return {
      left: "50%",
      transform: "translateX(-50%)",
      bottom: "calc(100% + 16px)",
      textAlign: "center",
    };
  }
  if (angle === 90) {
    return {
      left: "50%",
      transform: "translateX(-50%)",
      top: "calc(100% + 16px)",
      textAlign: "center",
    };
  }
  // Right side: 0, -45, 45 → label to the right of the node
  if (angle === 0) {
    return {
      left: "calc(100% + 18px)",
      top: "50%",
      transform: "translateY(-50%)",
      textAlign: "left",
    };
  }
  if (angle === -45) {
    // upper right → label sits to the right, baseline near the top of node
    return {
      left: "calc(100% + 14px)",
      bottom: "70%",
      textAlign: "left",
    };
  }
  if (angle === 45) {
    // lower right → label to the right, top near bottom of node
    return {
      left: "calc(100% + 14px)",
      top: "70%",
      textAlign: "left",
    };
  }
  // Left side: 180, -135, 135 → label to the left of node
  if (angle === 180) {
    return {
      right: "calc(100% + 18px)",
      top: "50%",
      transform: "translateY(-50%)",
      textAlign: "right",
    };
  }
  if (angle === -135) {
    return {
      right: "calc(100% + 14px)",
      bottom: "70%",
      textAlign: "right",
    };
  }
  // 135
  return {
    right: "calc(100% + 14px)",
    top: "70%",
    textAlign: "right",
  };
};

// Ray-ellipse intersection (centered at origin) → distance along direction (dx,dy)
// where ellipse equation is (x/a)^2 + (y/b)^2 = 1.
const rayEllipseDist = (dx, dy, a, b) => {
  // |t*dx/a|^2 + |t*dy/b|^2 = 1  →  t^2 = 1 / ( (dx/a)^2 + (dy/b)^2 )
  const denom = (dx * dx) / (a * a) + (dy * dy) / (b * b);
  return 1 / Math.sqrt(denom);
};

const FinanceOS = () => {
  const reduce = useReducedMotion();

  const handleNodeClick = useCallback((targetId) => {
    if (typeof window === "undefined") return;
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Quick highlight flash
    el.animate(
      [
        { boxShadow: "0 0 0 0 rgba(0,0,0,0)" },
        { boxShadow: "0 0 0 4px var(--col-accent, #15803d)" },
        { boxShadow: "0 0 0 0 rgba(0,0,0,0)" },
      ],
      { duration: 1600, easing: "ease-out" }
    );
  }, []);

  return (
    <section
      id="operating-system"
      data-testid="finance-os-section"
      className="relative py-24 md:py-32 border-t border-ink/15 bg-bone overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="mb-12 md:mb-16">
          <Reveal direction="up" delay={0.1}>
            <h2 className="font-display font-black uppercase text-[clamp(2rem,5.4vw,4.5rem)] leading-[0.95] tracking-[-0.02em] text-ink max-w-[20ch]">
              Bringing data-driven systems
              <br />
              <span className="text-money italic font-medium normal-case">for scale.</span>
            </h2>
          </Reveal>
        </div>

        {/* Diagram canvas */}
        <div className="relative mx-auto w-full" style={{ maxWidth: "1080px" }}>
          {/* Square diagram container — labels extend outside this square but stay within the wrapper */}
          <div
            className="relative mx-auto"
            style={{ width: "min(660px, 70%)", aspectRatio: "1 / 1" }}
          >
            {/* Soft halo behind hub */}
            {!reduce && (
              <motion.div
                aria-hidden
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-money/10 blur-3xl"
                style={{ width: "44%", height: "44%" }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
            )}

            {/* SVG connectors + endpoint dots */}
            <svg
              viewBox={`0 0 ${VB} ${VB}`}
              className="absolute inset-0 w-full h-full"
              aria-hidden
              preserveAspectRatio="xMidYMid meet"
            >
              {nodes.map((n, i) => {
                const p = polar(n.angle, RADIUS);
                const dx = Math.cos((n.angle * Math.PI) / 180);
                const dy = Math.sin((n.angle * Math.PI) / 180);
                // Start from ellipse edge of hub
                const hubDist = rayEllipseDist(dx, dy, HUB_RX, HUB_RY);
                const x1 = CENTER + dx * hubDist;
                const y1 = CENTER + dy * hubDist;
                // End at the CENTER of the node circle so the line clearly meets the node
                const x2 = p.x;
                const y2 = p.y;
                // Decorative endpoint dot just outside the node
                const dotX = p.x + dx * (NODE_R + 22);
                const dotY = p.y + dy * (NODE_R + 22);
                return (
                  <g key={`line-${n.id}`}>
                    <motion.line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={n.color}
                      strokeWidth="2.5"
                      strokeOpacity="0.9"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{
                        duration: 1,
                        delay: 0.5 + i * 0.08,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    />
                    <motion.circle
                      cx={dotX}
                      cy={dotY}
                      r="5"
                      fill={n.color}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{
                        duration: 0.5,
                        delay: 1 + i * 0.08,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    />
                  </g>
                );
              })}

              {/* Traveling pulses (decisions flowing out) */}
              {!reduce &&
                nodes.map((n, i) => {
                  const p = polar(n.angle, RADIUS);
                  const dx = Math.cos((n.angle * Math.PI) / 180);
                  const dy = Math.sin((n.angle * Math.PI) / 180);
                  const hubDist = rayEllipseDist(dx, dy, HUB_RX, HUB_RY);
                  const x1 = CENTER + dx * hubDist;
                  const y1 = CENTER + dy * hubDist;
                  const x2 = p.x;
                  const y2 = p.y;
                  return (
                    <motion.circle
                      key={`pulse-${n.id}`}
                      r="4.5"
                      fill={n.color}
                      initial={{ cx: x1, cy: y1, opacity: 0 }}
                      animate={{
                        cx: [x1, x2, x2],
                        cy: [y1, y2, y2],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 2.4,
                        delay: 1.8 + i * 0.25,
                        repeat: Infinity,
                        repeatDelay: 3,
                        ease: "easeInOut",
                      }}
                    />
                  );
                })}
            </svg>

            {/* Center hub (ellipse) — positioning wrapper to avoid framer-motion transform conflict */}
            <div
              className="absolute top-1/2 left-1/2"
              style={{
                width: `${(HUB_RX * 2 * 100) / VB}%`,
                height: `${(HUB_RY * 2 * 100) / VB}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full h-full"
              >
                {!reduce && (
                  <motion.div
                    className="absolute inset-0 rounded-[50%] bg-money/20"
                    animate={{ scale: [1, 1.25, 1], opacity: [0.4, 0, 0.4] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                )}
                <div className="relative w-full h-full rounded-[50%] bg-ink text-cream flex flex-col items-center justify-center text-center px-4 border border-ink shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55)]">
                  <BarChart3
                    className="w-7 h-7 md:w-9 md:h-9 text-cream mb-1.5"
                    strokeWidth={1.8}
                  />
                  <div className="font-display font-black uppercase text-xl md:text-3xl tracking-[-0.01em] leading-none">
                    Finance
                  </div>
                  <div className="mt-1.5 h-px w-8 bg-acid" />
                  <div className="font-mono text-[8px] md:text-[10px] uppercase tracking-[0.18em] text-cream/55 mt-1.5 leading-tight">
                    OS for decisions
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Satellite nodes */}
            {nodes.map((n, i) => {
              const p = polar(n.angle, RADIUS);
              const Icon = n.Icon;
              const leftPct = (p.x / VB) * 100;
              const topPct = (p.y / VB) * 100;
              const nodePct = (NODE_R * 2 * 100) / VB;
              const labelStyle = getLabelStyle(n.angle);

              return (
                <div
                  key={n.id}
                  className="absolute"
                  style={{
                    left: `${leftPct}%`,
                    top: `${topPct}%`,
                    width: `${nodePct}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{
                      duration: 0.55,
                      delay: 0.8 + i * 0.08,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative"
                  >
                  {/* Circle */}
                  <motion.button
                    type="button"
                    onClick={() => handleNodeClick(n.target)}
                    data-testid={`fos-node-${n.id}`}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 220, damping: 14 }}
                    aria-label={`${n.label} — jump to capability`}
                    className="relative aspect-square w-full rounded-full bg-cream flex items-center justify-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bone"
                    style={{
                      border: `2.5px solid ${n.color}`,
                      boxShadow: "0 8px 24px -10px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Icon
                      className="w-1/2 h-1/2"
                      strokeWidth={1.7}
                      style={{ color: n.color }}
                    />
                    {!reduce && (
                      <motion.span
                        aria-hidden
                        className="absolute inset-0 rounded-full"
                        style={{ border: `2px solid ${n.color}` }}
                        animate={{ scale: [1, 1.25], opacity: [0.4, 0] }}
                        transition={{
                          duration: 2.4,
                          delay: 1.4 + i * 0.18,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                      />
                    )}
                  </motion.button>

                  {/* Label */}
                  <div
                    className="absolute whitespace-nowrap pointer-events-none"
                    style={labelStyle}
                  >
                    <div
                      className="font-display font-bold uppercase text-[11px] sm:text-xs md:text-sm tracking-[0.06em] leading-tight"
                      style={{ color: n.color }}
                    >
                      {n.label}
                    </div>
                    <div className="font-sans-pro text-[10px] md:text-[11px] text-ink/65 leading-snug mt-0.5">
                      {n.desc}
                    </div>
                  </div>
                </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinanceOS;
